'use client';

import type { ReactNode } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../atoms/alert-dialog';
import type { ThemeScopeValue } from '../theme/constants';
import { cn } from '../utils';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title: string;
  /** Body copy — a node so a caller can emphasize part of it (e.g. a name). */
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  /**
   * Extra classes for the title. The atom's own color is `text-gray-900` /
   * `dark:text-white`, so a caller painting the panel dark WITHOUT a `.dark`
   * ancestor (the `(dashboard)` shell isn't dark-scoped) must pass the light
   * color here, or the heading renders near-black on near-black.
   */
  titleClassName?: string;
  /** Extra classes applied to the footer (e.g. "flex-row [&>*]:flex-1" for full-width buttons). */
  footerClassName?: string;
  /**
   * Extra classes applied to the dialog content (e.g. a raised `z-[100]` when
   * the confirm opens on top of another modal whose content already sits at the
   * default `z-[90]`).
   */
  contentClassName?: string;
  /**
   * Pins the portaled panel to one theme scope. The content lands on
   * `document.body`, so it sits OUTSIDE any `ThemeScope` the caller is inside
   * and would otherwise resolve its roles against `<html>` — the app theme,
   * dark by default. Pass the scope the caller itself is in (see
   * `useScopedTheme`) when that is not the app theme: the pre-auth login funnel
   * is pinned light, and without this its "No Account Found" confirm came up as
   * a black panel over a white page.
   */
  contentTheme?: ThemeScopeValue;
  /**
   * Extra classes for the confirm action, and for the cancel button. For a
   * caller whose surface has its own palette — the signup/login funnel's mint
   * CTA on near-black, say — where the role defaults (`surface-1` panel,
   * `raise`/`sink` action) read as a different product's dialog.
   */
  confirmClassName?: string;
  cancelClassName?: string;
  /**
   * Hide the confirm action entirely, leaving only the cancel/dismiss button.
   * Used when the confirm path isn't available in the current context (e.g.
   * signup is disabled for app users) — turns the dialog into an acknowledge-
   * only notice.
   */
  hideConfirm?: boolean;
  /** Disable the confirm action (e.g. gated on form validity). */
  confirmDisabled?: boolean;
  /**
   * Busy state for an async confirm (e.g. saving). Disables both actions while
   * true and swaps the confirm label for `loadingLabel`.
   */
  confirmLoading?: boolean;
  /** Confirm-button label while `confirmLoading` (default "Saving…"). */
  loadingLabel?: string;
  /** Optional icon rendered centered above the title (e.g. an amber alert). */
  icon?: ReactNode;
  /**
   * Run when the cancel button is clicked (in addition to closing). Use when the
   * cancel side is itself an action — e.g. "Discard" on an unsaved-changes
   * prompt. Omit for a plain dismiss.
   */
  onCancel?: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  titleClassName,
  footerClassName,
  contentClassName,
  contentTheme,
  confirmClassName,
  cancelClassName,
  hideConfirm = false,
  confirmDisabled = false,
  confirmLoading = false,
  loadingLabel = 'Saving…',
  icon,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        data-theme={contentTheme}
        className={cn(contentClassName)}
      >
        <AlertDialogHeader>
          {icon ? <div className="flex justify-center pb-1">{icon}</div> : null}
          <AlertDialogTitle className={titleClassName}>
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className={footerClassName}>
          <AlertDialogCancel
            onClick={onCancel}
            disabled={confirmLoading}
            className={cancelClassName}
          >
            {cancelLabel}
          </AlertDialogCancel>
          {!hideConfirm && (
            <AlertDialogAction
              onClick={onConfirm}
              disabled={confirmDisabled || confirmLoading}
              className={cn(
                variant === 'danger'
                  ? 'bg-danger text-content-on-danger hover:bg-red-600'
                  : // Develop drew this inverted — `bg-gray-900` in light,
                    // `dark:bg-white` in dark — and `raise`/`sink` mapped it
                    // faithfully, which is why light got a PURE BLACK button.
                    // Faithful, but off-palette: `action` is this product's
                    // primary-CTA fill (#12874B, white ink at 4.57:1), and it
                    // is what every other primary CTA on a creator surface
                    // already uses.
                    // `action` in BOTH creator themes — a primary button is the
                    // same button whichever theme the creator is in. The bare
                    // `bg-raise text-sink` remains only as the VISITOR base,
                    // where it resolves to a white button with black ink and
                    // must not change; `theme-light`/`theme-dark` never match
                    // that scope.
                    'bg-raise text-sink theme-light:bg-action theme-light:text-content-on-action theme-dark:bg-action theme-dark:text-content-on-action hover:opacity-90',
                confirmClassName,
              )}
            >
              {confirmLoading ? loadingLabel : confirmLabel}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
